import * as APIUtil from '../api/categories.api';
import {
    COUNTRIES_ORDER,
    DATA_SOURCES,
    HIDDEN_ROOTS,
} from '@/config/constants';
import {
    ChunkedRequestError,
    cachedFetch,
    endpointWrapper,
} from '../api/meta.api';
import { POPULARITY_METADATA_BY_KEY, TIKTOK_METADATA_BY_KEY } from '../metrics';
import {
    getCreators,
    getHashtags,
    getRelatedBrandHashtagData,
    getRelatedCategoryHashtagData,
    getRelatedHashtagData,
    getRelatedHashtagDataInPairs,
    getRelatedHashtagGroups,
    getTikTokData,
    getTikTokRelatedAffinityGroups,
    getTikTokRelatedBrandAggregate,
    getTikTokRelatedBrandBreakdownHashtags,
    getTikTokRelatedBrandKeywords,
    getTikTokRelatedBrandTopPosts,
    getTikTokRelatedCategoryAggregate,
    getTikTokRelatedCategoryBreakdownHashtags,
    getTikTokRelatedCategoryGroupAggregate,
    getTikTokRelatedCategoryKeywords,
    getTikTokRelatedCategoryPairs,
    getTikTokRelatedCategoryTopPosts,
    getTopPosts,
    getTopPostsHashtag,
    getTopPostsRelatedBrands,
    getTopPostsRelatedCategories,
    getTopPostsRelatedHashtags,
} from '../api/tiktok.api';
import {
    getInstagramRelatedAffinityGroups,
    getInstagramRelatedBrandAggregate,
    getInstagramRelatedBrandBreakdownHashtags,
    getInstagramRelatedBrandDrawerTopPosts,
    getInstagramRelatedBrandKeywords,
    getInstagramRelatedCategoryAggregate,
    getInstagramRelatedCategoryBreakdownHashtags,
    getInstagramRelatedCategoryDrawerTopPosts,
    getInstagramRelatedCategoryGroupAggregate,
    getInstagramRelatedCategoryKeywords,
    getInstagramRelatedCategoryPairs,
} from '../api/instagram.api';
import { shapeDataPoint } from 'state/utils';
import {
    apiDataSource,
    entityQueryKey,
    shapeEntities,
    shapeEntity,
} from 'utils/entities';
import { fetchEntities } from '../api/entities.api';
import { useCallback, useEffect, useMemo } from 'react';
import {
    useInfiniteQuery,
    useQueries,
    useQuery,
    useQueryClient,
} from 'react-query';
import { CaptionRegular } from '../../ui/components/Text';
import FilterButton from 'components/Button/FilterButton';
import FilterCheckboxChip from 'components/FilterChips/FilterCheckboxChip';
import Stack from 'components/Stack';
import { getFlavors } from '../api/flavors.api';
import { getPopIndexRelatedCategoryGroups } from '../api/popularity.api';

const fetchAffinityRoots = () =>
    cachedFetch(endpointWrapper)({
        domain: 'affinities',
        data: { parents: null, minimal: false },
    });

const key = 'affinityRoots';
const options = { staleTime: Infinity, cacheTime: Infinity };

export const usePrefetchAffinityRoots = () => {
    const qc = useQueryClient();

    useEffect(() => {
        qc.prefetchQuery(key, fetchAffinityRoots, options);
    }, []);

    return null;
};

export const useFetchAffinityRoots = () =>
    useQuery(key, fetchAffinityRoots, options);

const shapeAffinityRoot = ({ name, id, volume }) => ({
    label: name,
    volume,
    value: id,
});
const filterMostPopularGroups = (groups) => groups?.slice(0, 6);
const filterLessPopularGroups = (groups) => groups?.slice(6);
function NoOptionsMessage({ status, isLoading, isError, refetch }) {
    if (status === 'idle') return null;
    if (isError)
        return (
            <Stack $space=".5em;">
                Could not fetch groups
                <FilterButton
                    onClick={() => refetch()}
                    css={`
                        margin-top: ${({ theme }) => theme.space_4}px;
                    `}
                >
                    Retry
                </FilterButton>
            </Stack>
        );

    if (isLoading)
        return (
            <FilterCheckboxChip
                disabled={true}
                isLoading={true}
                checked={false}
            />
        );

    return <CaptionRegular>No additional groups available.</CaptionRegular>;
}

const fetchRelatedSearchGroupsWithData = async ({ queryKey }) => {
    const relatedSearchGroups = await fetchAffinityRoots().then(shapeEntities);
    const relatedSearchGroupsData = await APIUtil.fetchDataPoints({
        data: {
            ...queryKey[1],
            affinities: relatedSearchGroups.map(({ id }) => id),
        },
        paginateBy: 'affinities',
    }).then((resp) => {
        return resp.results.map((dp) => ({
            ...shapeDataPoint(dp),
            affinity: dp.affinity,
        }));
    });
    const relatedSearchGroupsById = relatedSearchGroups.reduce((acc, group) => {
        acc[group.id] = group;
        return acc;
    }, {});
    return relatedSearchGroupsData
        .map((dataPoint) => {
            const group = relatedSearchGroupsById[dataPoint.affinity];
            return { ...dataPoint, id: group.id, name: group.name };
        })
        .sort((a, b) => {
            return b.volume - a.volume;
        });
};

export const useRelatedSearchGroupsWithData = (config) => {
    const enabled = !!(
        config?.countries &&
        (config?.categories || config?.brands)
    );
    return useQuery(
        ['relatedSearchGroups', config],
        fetchRelatedSearchGroupsWithData,
        { enabled },
    );
};

const getFilteredAffinityRoots = (filterFn) => (config) => {
    const { data: initialData, ...restQuery } =
        useRelatedSearchGroupsWithData(config);
    let data = initialData;

    if (data) {
        data = filterFn(data.map(shapeAffinityRoot));
    }

    return { data, ...restQuery, NoOptionsMessage };
};

export const useFetchTikTokAffinityRoots = (props) => {
    return useQuery(
        ['tiktok/related_affinity_groups', props],
        ({ queryKey }) =>
            getTikTokRelatedAffinityGroups({
                data: {
                    country: queryKey[1].countries,
                    category: queryKey[1].categories,
                    brand: queryKey[1].brands,
                    week: queryKey[1].week ? [queryKey[1].week] : null,
                    year: queryKey[1].year ? [queryKey[1].year] : null,
                },
            }).then(({ data }) => data || []),
        { staleTime: 300000, cacheTime: 300000 },
    );
};

const shapeTikTokAffinityRoot = ({ name, id, volume }) => ({
    label: name,
    value: id,
    volume,
});

export const useFetchPopIndexAffinityRoots = (props) => {
    return useQuery(
        ['pop-index/related_category_groups', props],
        ({ queryKey }) =>
            getPopIndexRelatedCategoryGroups({
                data: {
                    country: queryKey[1].countries,
                    category: queryKey[1].categories,
                    brand: queryKey[1].brands,
                    order_by:
                        POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month
                            .key,
                    ascending: false,
                },
            }).then(({ data }) =>
                data?.map(({ group, data }) => ({
                    name: group?.toLowerCase(),
                    id: group?.toLowerCase(),
                    volume: data?.[
                        POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month
                            .key
                    ],
                })),
            ),
        { staleTime: 300000, cacheTime: 300000 },
    );
};

const shapePopIndexAffinityRoot = ({ name, id, volume }) => ({
    label: name,
    value: id,
    volume,
});

export const useFetchMostPopularAffinityRoots = getFilteredAffinityRoots(
    filterMostPopularGroups,
);
export const useFetchLessPopularAffinityRoots = getFilteredAffinityRoots(
    filterLessPopularGroups,
);

export const useFetchMostPopularTikTokAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchTikTokAffinityRoots(props);
    let data = initialData;
    if (data) {
        data = data.slice(0, 6).map(shapeTikTokAffinityRoot);
    }
    return { data, ...restQuery, NoOptionsMessage };
};

export const useFetchLessPopularTikTokAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchTikTokAffinityRoots(props);
    let data = initialData;
    if (data) {
        data = data.slice(6).map(shapeTikTokAffinityRoot);
    }
    return { data, ...restQuery, NoOptionsMessage };
};

export const useFetchMostPopularPopIndexAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchPopIndexAffinityRoots(props);
    let data = initialData;
    if (data) {
        data = data.slice(0, 6).map(shapePopIndexAffinityRoot);
    }
    return { data, ...restQuery, NoOptionsMessage };
};

export const useFetchLessPopularPopIndexAffinityRoots = (props) => {
    const { data: initialData, ...restQuery } =
        useFetchPopIndexAffinityRoots(props);
    let data = initialData;
    if (data) {
        data = data.slice(6).map(shapePopIndexAffinityRoot);
    }
    return { data, ...restQuery, NoOptionsMessage };
};

const getCountryKey = (country) => country.text_id || country.code;
const sortCountriesByOrder = (countries, order) => {
    countries.sort((a, b) => {
        const aKey = getCountryKey(a);
        const bKey = getCountryKey(b);
        const aIndex = order.indexOf(aKey);
        const bIndex = order.indexOf(bKey);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
    });
};
const fetchCountries = () =>
    cachedFetch(endpointWrapper)({
        domain: 'countries',
        data: { parents: null },
    }).then((countries) => {
        sortCountriesByOrder(countries, COUNTRIES_ORDER);
        return countries;
    });

export const useFetchCountries = () =>
    useQuery('countries', fetchCountries, options);

const fetchTikTokCountries = () =>
    cachedFetch(endpointWrapper)({
        domain: 'tiktok/countries',
        data: { parents: null },
    }).then((countries) => {
        sortCountriesByOrder(countries, COUNTRIES_ORDER);
        return countries;
    });

export const useFetchTikTokCountries = () =>
    useQuery('tiktokCountries', fetchTikTokCountries, options);

/**
 * Resolve one affinity ("related search group") by id.
 *
 * Google-only, and keeps its own lookup rather than being an entity type on the
 * per-source endpoint: affinities are slated to become related_categories.
 */
export const useAffinity = ({ id, enabled = true }) =>
    useQuery({
        queryKey: ['affinity', { id }],
        queryFn: async () => {
            const { results, errors } = await APIUtil.fetchAffinities({
                data: { ids: [id], minimal: true },
            });
            if (errors) throw new ChunkedRequestError(errors);
            return results?.[0] ? shapeEntity(results[0]) : null;
        },
        enabled: enabled && !!id,
        staleTime: Infinity,
    });

/**
 * Resolve a single entity by id, against one data source. Shares the
 * ['entity', …] key with the seeded cache, so an entity a page already
 * fetched is reused rather than refetched.
 */
export const useEntity = ({
    id,
    entityType = 'categories',
    dataSource,
    minimal = true,
    enabled = true,
}) => {
    const source = apiDataSource(dataSource);
    return useQuery({
        queryKey: entityQueryKey({ id, dataSource, minimal }),
        queryFn: async () => {
            const entities = await fetchEntities({
                dataSource: source,
                entityType,
                ids: [id],
                minimal,
            });
            const entity = entities?.[id];
            return entity ? shapeEntity(entity) : null;
        },
        enabled: enabled && !!id,
        staleTime: Infinity,
    });
};

/**
 * Resolve entities by id against one data source, for the standalone case — a
 * header, breadcrumb or drawer with no accompanying data_points call. Where a
 * data_points response is in hand, read its `entities` sidecar instead.
 */
export const useEntities = ({
    ids,
    entityType,
    dataSource,
    minimal = true,
    enabled = true,
    ...props
}) => {
    const source = apiDataSource(dataSource);
    const queryIds = useMemo(() => [...new Set(ids || [])].sort(), [ids]);

    const queryFn = useCallback(async ({ queryKey }) => {
        const { ids, entityType, source, minimal } = queryKey[1];
        const entities = await fetchEntities({
            dataSource: source,
            entityType,
            ids,
            minimal,
        });
        // Caller's id order; a missing entity drops out rather than holing.
        return ids
            .map((id) => entities?.[id])
            .filter(Boolean)
            .map(shapeEntity);
    }, []);

    return useQuery(
        ['entities', { ids: queryIds, entityType, source, minimal }],
        queryFn,
        {
            enabled: enabled && queryIds.length > 0,
            staleTime: Infinity,
            ...props,
        },
    );
};

/**
 * The cat5 (leaf) descendants of some root categories. Google only because that
 * is where the non-minimal shape is served, not because the data is Google's —
 * see the entities service README.
 */
export const useCatFives = ({ rootIds, allowList, enabled = true }) => {
    const query = useEntities({
        ids: rootIds,
        entityType: 'categories',
        dataSource: DATA_SOURCES.GOOGLE,
        minimal: false,
        enabled,
    });

    const catFives = useMemo(() => {
        if (!query.data) return null;
        const found = query.data.flatMap(({ cat_fives }) => cat_fives || []);
        if (!allowList) return found;
        const allowed = new Set(allowList);
        return found.filter((id) => allowed.has(id));
    }, [query.data, allowList]);

    return { ...query, data: catFives };
};

export const useTikTokDataPoints = ({
    per_page = 500,
    order_by,
    ascending,
    week,
    year,
    countries,
    brands,
    categories,
    hashtags,
    enabled = true,
    staleTime,
    placeholderData,
}) => {
    const queryFn = ({ queryKey }) =>
        getTikTokData({
            data: {
                ...queryKey[1],
                week: queryKey[1].week
                    ? queryKey[1].week === '*'
                        ? '*'
                        : [queryKey[1].week]
                    : null,
                year: queryKey[1].year
                    ? queryKey[1].year === '*'
                        ? '*'
                        : [queryKey[1].year]
                    : null,
            },
        });
    const getQueryKey = (page) => [
        'TikTokData',
        {
            week,
            year,
            order_by,
            ascending,
            country: countries,
            category: categories,
            brand: brands,
            hashtag: hashtags,
            per_page,
            page,
        },
    ];
    const firstPageQueryObject = {
        queryKey: getQueryKey(1),
        queryFn,
        enabled,
        staleTime,
    };
    const query = useQuery(firstPageQueryObject);
    const queries = useQueries(
        new Array(query.data?.total_pages || 1).fill(0).map((_, idx) => ({
            queryKey: getQueryKey(idx + 1),
            queryFn,
            enabled,
            staleTime,
            placeholderData: placeholderData?.length
                ? () => ({
                      data: placeholderData,
                      total_records: placeholderData?.length || 0,
                  })
                : undefined,
        })),
    );
    return queries;
};

export const getQueryKeyRelatedHashtagData = ({
    week,
    year,
    order_by,
    ascending,
    categories,
    brands,
    hashtags,
    countries,
    affinity_group,
    price_segmentation,
    search,
    per_page,
    fetch_in_pairs,
}) => {
    return [
        'TikTokRelatedHashtagData',
        {
            week,
            year,
            order_by,
            ascending,
            category: categories,
            brand: brands,
            hashtag: hashtags,
            country: countries,
            affinity_group,
            price_segmentation,
            search,
            per_page,
            fetch_in_pairs: !!fetch_in_pairs,
        },
    ];
};

export const useRelatedHashtagData = ({
    per_page = 500,
    order_by = 'non_cum_avg_weekly_views',
    ascending = false,
    week,
    year,
    brands,
    categories,
    hashtags,
    countries,
    affinity_group,
    price_segmentation,
    search,
    enabled = true,
    placeholderData,
    keepPreviousData = true,
    fetch_in_pairs = false,
}) => {
    const getRelatedHashtagDataFunction = fetch_in_pairs
        ? getRelatedHashtagDataInPairs
        : getRelatedHashtagData;
    const queryFn = ({ queryKey, pageParam = 1 }) =>
        getRelatedHashtagDataFunction({
            data: {
                ...queryKey[1],
                week: queryKey[1].week ? [queryKey[1].week] : null,
                year: queryKey[1].year ? [queryKey[1].year] : null,
                page: pageParam,
            },
        });
    const queryKey = getQueryKeyRelatedHashtagData({
        week,
        year,
        order_by,
        ascending,
        categories,
        brands,
        hashtags,
        countries,
        affinity_group,
        price_segmentation,
        search,
        per_page,
        fetch_in_pairs,
    });
    const query = useInfiniteQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
        getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
        keepPreviousData,
        placeholderData: placeholderData?.length
            ? () => ({
                  pages: [
                      {
                          data: placeholderData,
                          total_records: placeholderData?.length || 0,
                          last_page: 1,
                          messages: null,
                          next_page: null,
                          page: 1,
                          per_page,
                          prev_page: null,
                      },
                  ],
              })
            : undefined,
    });
    const getQueryData = async () => {
        const currentPages = query?.data?.pages || [];
        const proms = [];
        for (
            let i = currentPages.length + 1;
            i <= query?.data?.pages[0]?.last_page;
            i++
        ) {
            proms.push(queryFn({ queryKey, pageParam: i }));
        }
        const restPages = await Promise.all(proms);
        return [...currentPages, ...restPages].map(({ data }) => data).flat();
    };
    return {
        query,
        queryKey,
        getQueryData,
    };
};

export const useRelatedEntityHashtagData = ({
    per_page = 500,
    order_by = 'non_cum_avg_weekly_views',
    ascending = false,
    week,
    year,
    related_brands,
    related_categories,
    categories,
    brands,
    countries,
    affinity_group,
    enabled = true,
}) => {
    const queryFn = ({ queryKey }) =>
        (queryKey[1]?.related_brand
            ? getRelatedBrandHashtagData
            : getRelatedCategoryHashtagData)({
            data: {
                ...queryKey[1],
                week: queryKey[1].week ? [queryKey[1].week] : null,
                year: queryKey[1].year ? [queryKey[1].year] : null,
            },
        });
    const getQueryKey = (page) => [
        'TikTokRelatedEntityHashtagData',
        {
            week,
            year,
            order_by,
            ascending,
            related_category: related_categories,
            related_brand: related_brands,
            category: categories,
            brand: brands,
            country: countries,
            affinity_group,
            per_page,
            page,
        },
    ];
    const firstPageQueryObject = {
        queryKey: getQueryKey(1),
        queryFn,
        enabled,
        staleTime: Infinity,
    };
    const query = useQuery(firstPageQueryObject);
    const queries = useQueries(
        new Array(query.data?.last_page || 1).fill(0).map((_, idx) => ({
            queryKey: getQueryKey(idx + 1),
            queryFn,
            enabled,
            staleTime: Infinity,
        })),
    );
    return queries;
};

// -----------------------------------------------------------------------------
// Keyword + hashtag post aggregation surfaces (replaces legacy related_hashtag).
//
// The aggregate hooks use `useInfiniteQuery` like `useRelatedHashtagData` so the
// table component logic stays parallel. The drawer breakdown hooks use
// `useQuery` since the drawer caps results and does not paginate.
// -----------------------------------------------------------------------------

const buildAggregatePayload = ({ queryKey, pageParam = 1 }) => ({
    data: {
        ...queryKey[1],
        week: queryKey[1].week ? [queryKey[1].week] : null,
        year: queryKey[1].year ? [queryKey[1].year] : null,
        page: pageParam,
    },
});

const aggregateInfiniteQueryOptions = ({
    enabled,
    keepPreviousData = true,
}) => ({
    enabled,
    staleTime: Infinity,
    keepPreviousData,
    getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
});

const buildAggregateGetQueryData = (query, queryFn, queryKey) => async () => {
    const currentPages = query?.data?.pages || [];
    const lastPage = currentPages[0]?.last_page || 0;
    const proms = [];
    for (let i = currentPages.length + 1; i <= lastPage; i++) {
        proms.push(queryFn({ queryKey, pageParam: i }));
    }
    const restPages = await Promise.all(proms);
    return [...currentPages, ...restPages].map(({ data }) => data).flat();
};

// Each field list is an allow-list, not a shared union. The props hook builds
// ONE argument object carrying `brands: brands || null` and spreads it into
// whichever hook it drives, including the brand hooks, which take no `brands`.
// react-query hashes the key with JSON.stringify under an Object.keys().sort()
// replacer, so field order is irrelevant but the field set is not: absent and
// present-but-undefined hash alike, `brand: null` does not. Widening a hook
// moves its cache entry and, because `buildAggregatePayload` spreads
// queryKey[1] into the POST body, changes the request too.
// Keep the key names literal, not derived from the descriptor's display label.

const RELATED_AGGREGATE_FIELDS = {
    category: [
        'week',
        'year',
        'order_by',
        'ascending',
        'category',
        'brand',
        'country',
        'affinity_group',
        'price_segmentation',
        'search',
        'per_page',
    ],
    brand: [
        'week',
        'year',
        'order_by',
        'ascending',
        'category',
        'country',
        'price_segmentation',
        'search',
        'per_page',
    ],
    group: [
        'week',
        'year',
        'order_by',
        'ascending',
        'category',
        'brand',
        'country',
        'per_page',
    ],
};

// The key spells three params in the singular; the rest pass through unchanged.
const RELATED_AGGREGATE_PARAM_BY_FIELD = {
    category: 'categories',
    brand: 'brands',
    country: 'countries',
};

const createRelatedAggregateDataHook = ({
    keyName,
    fetchPage,
    kind,
    defaultOrderBy,
}) => {
    const fields = RELATED_AGGREGATE_FIELDS[kind];
    return ({
        per_page = 50,
        order_by = defaultOrderBy,
        ascending = false,
        enabled = true,
        keepPreviousData = true,
        ...rest
    }) => {
        const args = { ...rest, per_page, order_by, ascending };
        const queryKey = [
            keyName,
            fields.reduce((payload, field) => {
                payload[field] =
                    args[RELATED_AGGREGATE_PARAM_BY_FIELD[field] || field];
                return payload;
            }, {}),
        ];
        const queryFn = (ctx) => fetchPage(buildAggregatePayload(ctx));
        const query = useInfiniteQuery(
            queryKey,
            queryFn,
            aggregateInfiniteQueryOptions({
                enabled: enabled && !!(rest.week && rest.year),
                keepPreviousData,
            }),
        );
        return {
            query,
            queryKey,
            getQueryData: buildAggregateGetQueryData(query, queryFn, queryKey),
        };
    };
};

export const useTikTokRelatedCategoryData = createRelatedAggregateDataHook({
    keyName: 'TikTokRelatedCategoryAggregate',
    fetchPage: getTikTokRelatedCategoryAggregate,
    kind: 'category',
    defaultOrderBy: 'non_cum_avg_weekly_views',
});

export const useTikTokRelatedBrandData = createRelatedAggregateDataHook({
    keyName: 'TikTokRelatedBrandAggregate',
    fetchPage: getTikTokRelatedBrandAggregate,
    kind: 'brand',
    defaultOrderBy: 'non_cum_avg_weekly_views',
});

export const useTikTokRelatedCategoryGroupData = createRelatedAggregateDataHook(
    {
        keyName: 'TikTokRelatedCategoryGroupAggregate',
        fetchPage: getTikTokRelatedCategoryGroupAggregate,
        kind: 'group',
        defaultOrderBy: 'non_cum_avg_weekly_views',
    },
);

// Drawer breakdown hooks. The drawer shows ~5 chips by default with a
// "Show More" expansion; per_page defaults match that limit.

// Watchlist enrichment for `related-category` items in the TT tab. Takes
// parallel `(categoryId, relatedCategoryId)` and `(brandId, relatedCategoryId)`
// pairs and returns the latest aggregate row for each — the keyword-matching
// equivalent of the legacy `tiktok_data_points_related_hashtag_pairs` flow.
// The BE checks both `category_related_category` and `category_related_brand`
// for cat-parent pairs and stamps `related_type` on the response so the FE
// drawer can route correctly.
export const useTikTokWatchlistRelatedCategoryData = ({
    categoryPairs = [],
    brandPairs = [],
    countries,
    week,
    year,
    enabled = true,
    placeholderData,
}) =>
    useQuery({
        queryKey: [
            'TikTokWatchlistRelatedCategoryPairs',
            {
                category_pairs: categoryPairs,
                brand_pairs: brandPairs,
                country: countries || null,
                week: week ? [week] : null,
                year: year ? [year] : null,
            },
        ],
        queryFn: ({ queryKey }) =>
            getTikTokRelatedCategoryPairs({ data: queryKey[1] }),
        enabled:
            enabled &&
            !!(week && year) &&
            !!(categoryPairs.length || brandPairs.length),
        staleTime: Infinity,
        // Discard prior data when the queryKey changes so a delete drops the
        // row immediately; `placeholderData` (set by `handleQueryCacheOn-
        // RemoveItem`) bridges to the freshly fetched data without a blank.
        keepPreviousData: false,
        placeholderData: placeholderData?.length
            ? { data: placeholderData }
            : undefined,
    });

export const useTikTokRelatedDrawerKeywords = ({
    entity, // 'category' | 'brand' — type of related entity
    page = 1,
    per_page = 50,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'TikTokRelatedDrawerKeywords',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getTikTokRelatedBrandKeywords
                : getTikTokRelatedCategoryKeywords)({ data: queryKey[1] }),
        enabled: !!(week && year && enabled),
    });

export const useTikTokRelatedDrawerHashtags = ({
    entity,
    page = 1,
    per_page = 50,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'TikTokRelatedDrawerHashtags',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getTikTokRelatedBrandBreakdownHashtags
                : getTikTokRelatedCategoryBreakdownHashtags)({
                data: queryKey[1],
            }),
        enabled: !!(week && year && enabled),
    });

export const useTikTokRelatedDrawerTopPosts = ({
    entity,
    page = 1,
    per_page = 25,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    rank_type = 'total_post_views',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'TikTokRelatedDrawerTopPosts',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                rank_type,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getTikTokRelatedBrandTopPosts
                : getTikTokRelatedCategoryTopPosts)({ data: queryKey[1] }),
        enabled: !!(week && year && enabled),
    });

// -----------------------------------------------------------------------------
// Instagram Related Categories / Brands — mirrors the TT hooks above.
// Reads from the IG v2 `data_store_*_related_*` tables introduced by the
// keyword-matching project.
// -----------------------------------------------------------------------------

export const useInstagramRelatedCategoryData = createRelatedAggregateDataHook({
    keyName: 'InstagramRelatedCategoryAggregate',
    fetchPage: getInstagramRelatedCategoryAggregate,
    kind: 'category',
    defaultOrderBy: 'non_cum_avg_weekly_likes',
});

export const useInstagramRelatedBrandData = createRelatedAggregateDataHook({
    keyName: 'InstagramRelatedBrandAggregate',
    fetchPage: getInstagramRelatedBrandAggregate,
    kind: 'brand',
    defaultOrderBy: 'non_cum_avg_weekly_likes',
});

export const useInstagramRelatedCategoryGroupData =
    createRelatedAggregateDataHook({
        keyName: 'InstagramRelatedCategoryGroupAggregate',
        fetchPage: getInstagramRelatedCategoryGroupAggregate,
        kind: 'group',
        defaultOrderBy: 'non_cum_avg_weekly_likes',
    });

export const useInstagramWatchlistRelatedCategoryData = ({
    categoryPairs = [],
    brandPairs = [],
    countries,
    week,
    year,
    enabled = true,
    placeholderData,
}) =>
    useQuery({
        queryKey: [
            'InstagramWatchlistRelatedCategoryPairs',
            {
                category_pairs: categoryPairs,
                brand_pairs: brandPairs,
                country: countries || null,
                week: week ? [week] : null,
                year: year ? [year] : null,
            },
        ],
        queryFn: ({ queryKey }) =>
            getInstagramRelatedCategoryPairs({ data: queryKey[1] }),
        enabled:
            enabled &&
            !!(week && year) &&
            !!(categoryPairs.length || brandPairs.length),
        staleTime: Infinity,
        keepPreviousData: false,
        placeholderData: placeholderData?.length
            ? { data: placeholderData }
            : undefined,
    });

export const useInstagramRelatedDrawerKeywords = ({
    entity,
    page = 1,
    per_page = 50,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'InstagramRelatedDrawerKeywords',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getInstagramRelatedBrandKeywords
                : getInstagramRelatedCategoryKeywords)({ data: queryKey[1] }),
        enabled: !!(week && year && enabled),
    });

export const useInstagramRelatedDrawerHashtags = ({
    entity,
    page = 1,
    per_page = 50,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'InstagramRelatedDrawerHashtags',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getInstagramRelatedBrandBreakdownHashtags
                : getInstagramRelatedCategoryBreakdownHashtags)({
                data: queryKey[1],
            }),
        enabled: !!(week && year && enabled),
    });

export const useInstagramRelatedDrawerTopPosts = ({
    entity,
    page = 1,
    per_page = 25,
    week,
    year,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    rank_type = 'total_post_likes',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'InstagramRelatedDrawerTopPosts',
            {
                entity,
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                category: categories || null,
                brand: brands || null,
                related_category: relatedCategories || null,
                related_brand: relatedBrands || null,
                rank_type,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1].entity === 'brand'
                ? getInstagramRelatedBrandDrawerTopPosts
                : getInstagramRelatedCategoryDrawerTopPosts)({
                data: queryKey[1],
            }),
        enabled: !!(week && year && enabled),
    });

// Replace the legacy `useFetchInstagramAffinityRoots` source. Same shape so
// downstream FilterSelect etc. don't change.
export const useFetchInstagramRelatedAffinityRoots = (props) =>
    useQuery(
        ['instagram/related_affinity_groups', props],
        ({ queryKey }) =>
            getInstagramRelatedAffinityGroups({
                data: {
                    country: queryKey[1].countries,
                    category: queryKey[1].categories,
                    brand: queryKey[1].brands,
                    week: queryKey[1].week ? [queryKey[1].week] : null,
                    year: queryKey[1].year ? [queryKey[1].year] : null,
                },
            }).then(({ data }) => data || []),
        { staleTime: 300000, cacheTime: 300000 },
    );

export const useHashtags = ({ ids, page = 1 }) =>
    useQuery({
        queryKey: ['hashtags', { ids, page }],
        queryFn: ({ queryKey }) => getHashtags({ data: queryKey[1] }),
        enabled: !!ids,
    });

export const useTopPosts = ({
    page = 1,
    per_page = 500,
    week,
    year,
    brands,
    categories,
    rank_type = 'total_post_views',
    order_by = 'post_rank',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'top-posts',
            {
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                brand: brands || null,
                category: categories || null,
                rank_type: rank_type ? [rank_type] : null,
                country: countries || null,
                order_by,
            },
        ],
        queryFn: ({ queryKey }) => getTopPosts({ data: queryKey[1] }),
        enabled: !!(week && year && (brands || categories) && enabled),
    });

export const useTopPostsHashtag = ({
    page = 1,
    per_page = 500,
    week,
    year,
    hashtags,
    rank_type = 'total_post_views',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'top-posts',
            {
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                hashtag: hashtags || null,
                rank_type: rank_type ? [rank_type] : null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) => getTopPostsHashtag({ data: queryKey[1] }),
        enabled: !!(week && year && hashtags && enabled),
    });

export const useTopPostsRelatedHashtags = ({
    page = 1,
    per_page = 25,
    week,
    year,
    brands,
    categories,
    hashtags,
    rank_type = 'total_post_views',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'top-posts-related-hashtags',
            {
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                brand: brands || null,
                category: categories || null,
                hashtag: hashtags || null,
                rank_type: rank_type ? [rank_type] : null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            getTopPostsRelatedHashtags({ data: queryKey[1] }),
        enabled: !!(
            week &&
            year &&
            hashtags &&
            (brands || categories) &&
            enabled
        ),
    });

export const useTopPostsRelatedEntities = ({
    page = 1,
    per_page = 25,
    week,
    year,
    related_brands,
    related_categories,
    brands,
    categories,
    rank_type = 'total_post_views',
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'top-posts-related-entities',
            {
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                related_category: related_categories,
                related_brand: related_brands,
                brand: brands,
                category: categories,
                rank_type: rank_type ? [rank_type] : null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) =>
            (queryKey[1]?.related_brand
                ? getTopPostsRelatedBrands
                : getTopPostsRelatedCategories)({ data: queryKey[1] }),
        enabled: !!(week && year && (brands || categories) && enabled),
    });

export const useCreators = ({
    page = 1,
    per_page = 25,
    week,
    year,
    brands,
    categories,
    hashtags,
    rank_type = 'last_week_likes',
    orderBy,
    countries,
    enabled = true,
}) =>
    useQuery({
        queryKey: [
            'tiktok-creators',
            {
                page,
                per_page,
                week: week ? [week] : null,
                year: year ? [year] : null,
                brand: brands || null,
                category: categories || null,
                hashtag: hashtags || null,
                rank_type: rank_type || null,
                order_by: orderBy || null,
                country: countries || null,
            },
        ],
        queryFn: ({ queryKey }) => getCreators({ data: queryKey[1] }),
        enabled: !!(
            week &&
            year &&
            (hashtags || brands || categories) &&
            enabled
        ),
    });

export const useAffinityCategoryLinks = ({
    affinities,
    categories,
    countries,
    hiddenRoots = HIDDEN_ROOTS,
    ...props
}) => {
    return useQuery({
        queryKey: [
            'affinity-category-links',
            { affinities, categories, countries, hiddenRoots },
        ],
        queryFn: ({ queryKey }) =>
            endpointWrapper({
                domain: 'affinity-category-links',
                data: queryKey[1],
            }),
        ...props,
    });
};

export const useAffinityGroupCategoryChildren = ({
    categories,
    countries,
    hiddenRoots = HIDDEN_ROOTS,
    ...props
}) => {
    return useQuery({
        queryKey: [
            'affinity--group-category-children',
            { categories, countries, hiddenRoots },
        ],
        queryFn: ({ queryKey }) =>
            endpointWrapper({
                domain: 'affinity-group-category-children',
                data: queryKey[1],
            }),
        ...props,
    });
};

export const useCategoryDescriptions = ({ categories, ...props }) => {
    return useQuery({
        queryKey: ['category-descriptions', { categories }],
        queryFn: ({ queryKey }) =>
            endpointWrapper({
                domain: 'category_descriptions',
                data: queryKey[1],
            }),
        enabled: !!(categories && categories.length > 0),
        ...props,
    });
};

export const useFetchFlavors = () =>
    useQuery({
        queryKey: ['FlavorsFilters'],
        queryFn: getFlavors,
        staleTime: Infinity,
    });
